import {useId, startTransition, useRef, useState} from 'react'
import {CircleIcon, StopIcon} from '@primer/octicons-react'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useAnalytics} from '@github-ui/use-analytics'
import {useQueryClient} from '@github-ui/react-query'
import {Banner, BranchName, Button, ConfirmationDialog, Dialog, Flash, NavList} from '@primer/react'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import type {
  StackSectionStatusType,
  StackSectionEntryType,
} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {
  useStackRebaseMutation,
  useStackRebaseStatus,
  type StackRebasePullRequestStatus,
} from '../../hooks/mutations/use-stack-rebase-mutation'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {AliveChannels as MergeBoxAliveChannels} from '../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../page-data/payloads/status-checks'
import {useMergeabilityLiveUpdates} from '../../hooks/use-mergeability-live-updates'
import {useStackMergeabilityQuery} from '../../page-data/loaders/use-stack-mergeability-page-data'
import {useMergeMethodContext} from '../../contexts/MergeMethodContext'
import {EntryStatusLabel} from './stack-section/EntryStatusLabel'
import {getStackEntryLineClass} from '../../helpers/get-stack-entry-line-class'
import {StackStatusIcon} from './stack-section/StatusIcon'
import {EntryStatusIcon} from './stack-section/EntryStatusIcon'
import {useDisableAutoMergeMutation} from '../../hooks/mutations/use-disable-auto-merge-mutation'

export function StackSection({
  pullRequestId,
  status,
  loadingEntryCount,
  entries,
  baseRefName,
  showRebaseButton = true,
  stackBaseRefRequiresSignatures = false,
  isStackMergeInProgress = false,
  viewerCanDisableAutoMerge = false,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  loadingEntryCount: number
  entries: StackSectionEntryType[]
  baseRefName?: string
  showRebaseButton?: boolean
  stackBaseRefRequiresSignatures?: boolean
  isStackMergeInProgress?: boolean
  viewerCanDisableAutoMerge?: boolean
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED' || showRebaseButton,
  )
  const {isRebasing, status: rebaseState} = useStackRebaseStatus()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [cancelMergeError, setCancelMergeError] = useState<string | undefined>()

  const {mutate: disableAutoMerge, isPending: isCancelMergePending} = useDisableAutoMergeMutation({
    onError: (e: Error) => {
      setCancelMergeError(e.message)
    },
  })

  const handleCancelMerge = () => {
    if (isCancelMergePending) return
    setCancelMergeError(undefined)
    disableAutoMerge()
  }
  const showRebaseSummary = Object.keys(rebaseState).length > 0 && !isRebasing

  const {sendAnalyticsEvent} = useAnalytics()
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()
  const {pathname} = useLocation()

  // Preserve the current tab (e.g. /changes, /commits) when navigating between stacked PRs
  const tabSuffix = getPullRequestTabSuffix(pathname)

  const onSuccessfulRebase = async () => {
    await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
  }

  const handleRebaseError = (e: Error) => {
    setErrorMessage(e.message)
  }

  const {mutate: rebaseStack} = useStackRebaseMutation({
    onSuccess: onSuccessfulRebase,
    onError: handleRebaseError,
  })

  const handleRebaseStack = () => {
    if (isRebasing) return
    setErrorMessage(null)
    rebaseStack({entries})
    sendAnalyticsEvent('conflicts_section.update_branch', 'MERGEBOX_CONFLICTS_SECTION_UPDATE_BRANCH_BUTTON')
  }

  const otherEntriesToMerge = entries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')

  // Compute which BEFORE entries can merge immediately (e.g. all before entries have 'OK' status)
  const canMergeMap = new Map<number, boolean>()
  let allBelowPass = true
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i]!
    if (e.position !== 'BEFORE' || e.pull.state === 'MERGED') continue
    canMergeMap.set(e.pull.number, e.status === 'OK' && allBelowPass)
    if (e.status !== 'OK') {
      allBelowPass = false
    }
  }

  let title
  let subtitle
  let anyPullsAreLoading = false
  const isFirstUnmergedPRInStack = otherEntriesToMerge.length === 0

  const entriesToMergeCount = entries.filter(
    e => (e.position === 'BEFORE' || e.position === 'CURRENT') && e.pull.state !== 'MERGED',
  ).length

  switch (true) {
    case isStackMergeInProgress && entriesToMergeCount === 1:
      title = 'Merging the first pull request in the stack…'
      subtitle = 'This pull request is being merged.'
      break
    case isStackMergeInProgress:
      title = 'Merging stack…'
      subtitle = `${entriesToMergeCount} pull requests are being merged.`
      break
    case isRebasing && Object.keys(rebaseState).length > 0: {
      const states = Object.values(rebaseState)
      const pendingCount = states.filter(s => s === 'PENDING').length
      const succeededCount = states.filter(s => s === 'SUCCEEDED').length
      const failedCount = states.filter(s => s === 'FAILED' || s === 'CONFLICTED').length
      title = 'Rebasing stack…'
      const parts: string[] = []
      if (pendingCount > 0) parts.push(`${pendingCount} in progress`)
      if (succeededCount > 0) parts.push(`${succeededCount} completed`)
      if (failedCount > 0) parts.push(`${failedCount} failed`)
      subtitle = parts.join(', ')
      break
    }
    case isRebasing:
      title = 'Rebasing stack…'
      subtitle = 'Starting rebase.'
      break
    case (status === 'FAILED' || status === 'BLOCKED') && isFirstUnmergedPRInStack:
      title = 'Unable to merge the first pull request in the stack'
      subtitle = 'This pull request cannot be merged.'
      break
    case status === 'FAILED' || status === 'BLOCKED':
      title = 'Unable to merge as a stack'
      subtitle = 'Some of the pull requests in this stack cannot be merged.'
      break
    case status === 'PASSED' && isFirstUnmergedPRInStack:
      title = 'Able to merge the first pull request in the stack'
      subtitle = 'Only this pull request will be merged.'
      break
    case status === 'PASSED':
      title = 'Able to merge as a stack'
      subtitle = `Merging this pull request will also merge ${otherEntriesToMerge.length} pull request${otherEntriesToMerge.length === 1 ? '' : 's'} below it.`
      break
    case status === 'LOADING_MERGE_STATE':
      anyPullsAreLoading = true
      title = 'Loading stack status...'
      subtitle = `${loadingEntryCount} of ${entries.length} pull requests loading`
      break
    default:
      title = 'Could not determine stack status'
      subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={stackSectionAriaId}
      className={`${styles.StackSection} border-bottom color-border-subtle`}
    >
      <MergeBoxSectionHeader
        headerId={stackSectionAriaId}
        title={title}
        subtitle={subtitle}
        icon={
          isRebasing || anyPullsAreLoading || isStackMergeInProgress ? (
            <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
          ) : (
            <StackStatusIcon status={status} isFirstUnmergedPRInStack={isFirstUnmergedPRInStack} />
          )
        }
        {...(isStackMergeInProgress
          ? {
              rightSideContent: (
                <Button
                  disabled={!viewerCanDisableAutoMerge}
                  onClick={handleCancelMerge}
                  loading={isCancelMergePending}
                  loadingAnnouncement="Cancelling stack merge"
                >
                  Cancel merge
                </Button>
              ),
            }
          : {
              expandableProps: {
                ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
                isExpanded: stackExpanded,
                onToggle: () => {
                  setStackExpanded(!stackExpanded)
                },
              },
            })}
      />
      <MergeBoxExpandable isExpanded={isStackMergeInProgress || stackExpanded}>
        {showRebaseButton && !isRebasing && (
          <RebaseButtonBanner
            onRebase={handleRebaseStack}
            outOfDateCount={entries.filter(e => e.pull.state !== 'MERGED').length}
            stackBaseRefRequiresSignatures={stackBaseRefRequiresSignatures}
          />
        )}
        {showRebaseSummary && <RebaseSummaryBanner rebaseState={rebaseState} />}
        <NavList className={styles.stackNavList}>
          {entries.map((e, idx) => {
            const previousEntry = entries[idx - 1]
            return (
              <NavList.Item
                key={e.pull.number}
                href={`${e.pull.url}${tabSuffix}`}
                aria-current={e.position === 'CURRENT'}
                className={getStackEntryLineClass(
                  e,
                  rebaseState[e.pull.number],
                  previousEntry,
                  previousEntry && rebaseState[previousEntry.pull.number],
                )}
              >
                <NavList.LeadingVisual>
                  <EntryStatusIcon entry={e} rebaseState={rebaseState[e.pull.number]} />
                </NavList.LeadingVisual>
                <span className={e.position === 'AFTER' ? 'fgColor-muted' : ''}>{e.pull.title}</span>
                <NavList.Description variant="block">
                  <span className="fgColor-muted">
                    #{e.pull.number} · {e.pull.headBranch}
                  </span>
                </NavList.Description>
                <NavList.TrailingVisual>
                  <EntryStatusLabel
                    entry={e}
                    rebaseState={rebaseState[e.pull.number]}
                    allBelowCanMerge={canMergeMap.get(e.pull.number) ?? false}
                  />
                </NavList.TrailingVisual>
                {!!e.channels && <EntryMergeabilityUpdateListener basePageDataUrl={e.pull.url} {...e.channels} />}
              </NavList.Item>
            )
          })}
          {baseRefName && (
            <li className={styles.branchItem}>
              <span className={styles.branchItemContent}>
                <CircleIcon size={12} className="fgColor-muted" />
                <BranchName as="span">{baseRefName}</BranchName>
              </span>
            </li>
          )}
        </NavList>
      </MergeBoxExpandable>
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
      {cancelMergeError && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {cancelMergeError}
        </Flash>
      )}
    </section>
  )
}

function EntryMergeabilityUpdateListener({
  basePageDataUrl,
  ...channels
}: {
  basePageDataUrl: string
} & MergeBoxAliveChannels &
  StatusChecksAliveChannels) {
  const {mergeMethod} = useMergeMethodContext()
  const {refetch} = useStackMergeabilityQuery({basePageDataUrl, mergeMethod, enabled: false})
  useMergeabilityLiveUpdates({
    channels,
    refetchQuery: () => {
      startTransition(() => {
        refetch()
      })
    },
    throttleTime: 5000,
  })
  return null
}

function RebaseButtonBanner({
  onRebase,
  outOfDateCount,
  stackBaseRefRequiresSignatures,
}: {
  onRebase: () => void
  outOfDateCount: number
  stackBaseRefRequiresSignatures: boolean
}) {
  const [showDialog, setShowDialog] = useState(false)
  const primaryActionRef = useRef<HTMLButtonElement>(null)
  return (
    <>
      <Banner
        className="tmp-m-2"
        variant="warning"
        hideTitle
        title="This stack needs to be rebased"
        description="Some pull requests in this stack are out of date with previous changes and need to be rebased."
        primaryAction={
          <Banner.PrimaryAction ref={primaryActionRef} onClick={() => setShowDialog(true)}>
            Rebase stack
          </Banner.PrimaryAction>
        }
      />
      {showDialog && stackBaseRefRequiresSignatures && (
        <Dialog
          className={styles.RebaseConfirmationDialog}
          title="Rebase stack via the command line"
          returnFocusRef={primaryActionRef}
          onClose={() => {
            setShowDialog(false)
            primaryActionRef.current?.focus()
          }}
        >
          <p className="mb-2">
            Stacked pull requests rely on rebasing rather than merge commits to update your branch. Instead of merging
            upstream changes into your branch, a rebase will replay the commits from your head branch on top of the
            latest base.
          </p>
          <h4 className="mb-2">CLI Instructions</h4>
          <p className="mb-1">
            Use the <a href="https://gh.io/stacks">gh stack</a> CLI to rebase your stack of branches and push your
            changes to remote.
          </p>
          <p className="mb-2 mt-3">
            <strong>Step 1</strong> Switch to the head branch of the pull request in the stack.
          </p>
          <div className={styles.codeBlock}>
            <pre>
              <code>gh stack checkout [branch]</code>
            </pre>
            <CopyToClipboardButton
              textToCopy="gh stack checkout [branch]"
              ariaLabel="Copy checkout command to clipboard"
              size="small"
            />
          </div>
          <p className="mb-2 mt-3">
            <strong>Step 2</strong> Rebase the stack with changes from the base branch.
          </p>
          <div className={styles.codeBlock}>
            <pre>
              <code>gh stack rebase</code>
            </pre>
            <CopyToClipboardButton
              textToCopy="gh stack rebase"
              ariaLabel="Copy rebase command to clipboard"
              size="small"
            />
          </div>
          <p className="mb-2 mt-3">
            <strong>Step 3</strong> Fix any conflicts and commit the result.
          </p>
          <p className="mt-2">
            See <a href="https://gh.io/stacks-resolving-conflicts">Resolving a merge conflict</a> for step-by-step
            instructions on resolving merge conflicts.
          </p>
          <p className="mb-2 mt-3">
            <strong>Step 4</strong> Push the changes.
          </p>
          <div className={styles.codeBlock}>
            <pre>
              <code>gh stack push</code>
            </pre>
            <CopyToClipboardButton textToCopy="gh stack push" ariaLabel="Copy push command to clipboard" size="small" />
          </div>
        </Dialog>
      )}
      {showDialog && !stackBaseRefRequiresSignatures && (
        <ConfirmationDialog
          className={styles.RebaseConfirmationDialog}
          title="Rebase stack"
          onClose={gesture => {
            if (gesture === 'confirm') {
              onRebase()
            }
            setShowDialog(false)
            primaryActionRef.current?.focus()
          }}
          confirmButtonContent={`Rebase ${outOfDateCount} pull request${outOfDateCount === 1 ? '' : 's'}`}
          confirmButtonType="primary"
        >
          <p className="mb-2">
            This will update {outOfDateCount} out-of-date pull request{outOfDateCount === 1 ? '' : 's'} in this stack:
          </p>
          <ul className="pl-4">
            <li>Each pull request will be rebased on top of the latest changes from its base branch</li>
            <li>The rebased branches will be force pushed to update the remote</li>
          </ul>
          <p className="mt-2">
            <a href="https://github.github.com/gh-stack/guides/workflows/#making-mid-stack-changes">
              Learn more about rebasing a stack
            </a>
          </p>
        </ConfirmationDialog>
      )}
    </>
  )
}

function RebaseSummaryBanner({rebaseState}: {rebaseState: Record<number, StackRebasePullRequestStatus>}) {
  const states = Object.values(rebaseState)
  const succeededCount = states.filter(s => s === 'SUCCEEDED').length
  const failedCount = states.filter(s => s === 'FAILED' || s === 'CONFLICTED').length

  const [hideSuccessBanner, setHideSuccessBanner] = useState(false)
  const [hideFailureBanner, setHideFailureBanner] = useState(false)
  if (states.length === 0) return null

  const allSucceeded = failedCount === 0

  let succeededDescription = ''
  let failedDescription = ''
  if (allSucceeded) {
    succeededDescription = `Successfully rebased ${succeededCount} pull request${succeededCount === 1 ? '' : 's'}.`
  } else {
    if (succeededCount > 0) {
      succeededDescription = `${succeededCount} pull request${succeededCount === 1 ? '' : 's'} rebased successfully.`
    }
    failedDescription = `${failedCount} pull request${failedCount === 1 ? '' : 's'} failed to rebase.`
  }

  return (
    <>
      {succeededCount > 0 && !hideSuccessBanner && (
        <Banner
          className="tmp-m-1"
          aria-label="Rebase success summary"
          variant={'success'}
          title="Rebase complete"
          hideTitle
          description={succeededDescription}
          onDismiss={() => setHideSuccessBanner(true)}
        />
      )}
      {failedCount > 0 && !hideFailureBanner && (
        <Banner
          className="tmp-m-1"
          aria-label="Rebase failure summary"
          variant={'critical'}
          title="Rebase failed"
          hideTitle
          description={failedDescription}
          onDismiss={() => setHideFailureBanner(true)}
        />
      )}
    </>
  )
}
